import {
  AgentEffectChoices,
  Card,
  Choice,
  ChoiceResult,
  Game,
  GameCommandByPlayer,
  getCommandArg,
} from "~/business";
import { Colors, Sizes, Spacing } from "~/jsx/theme";
import { Proposition, expectNever, getUndoCommandArg } from "~/utils";
import { XChoice } from "../game/XChoice";
import { XActionSlot } from "./XActionSlot";
import { XAgentEffect } from "./XAgentEffect";
import { XButton } from "./XButton";
import { XCard } from "./XCard";
import { XIntrigueCard } from "./XIntrigueCard";
import { XPlayerDreadnought } from "./XPlayerDreadnought";
import { XPlayerTroop } from "./XPlayerTroop";
import { XTechTile } from "./XTechTile";

export const XGameCommandEvent = ({
  event,
  game,
}: {
  event: GameCommandByPlayer;
  game: Game;
}) => {
  const { methodName, playerId } = event;
  const player = game.getPlayer(playerId);
  switch (methodName) {
    case "play": {
      const { cardId, actionSlotId } = getCommandArg<typeof methodName>(event);
      const card = player.getCard(cardId);
      const actionSlot = game.getActionSlot(actionSlotId);

      return (
        <>
          plays <XEventCard card={card} size={92} /> on slot{" "}
          <div
            style={{
              width: "fit-content",
              display: "inline-block",
              verticalAlign: "middle",
            }}
          >
            <XActionSlot actionSlot={actionSlot} noAnimation={true} />
          </div>
        </>
      );
    }
    case "buyCard": {
      const cardId = getCommandArg<typeof methodName>(event);
      const card = game.findCard(cardId)!;
      return (
        <>
          buys <XEventCard card={card} size={128} />
        </>
      );
    }
    case "endBuyPhase": {
      return null;
    }
    case "playIntrigue": {
      const cardId = getCommandArg<typeof methodName>(event);
      const intrigueCard = player.intrigueCards.getById(cardId);
      return (
        <>
          plays{" "}
          <XIntrigueCard width={Sizes.intrigueCard.width} card={intrigueCard} />
        </>
      );
    }
    case "revealAll": {
      return null;
    }
    case "passIntrigue": {
      return null;
    }
    case "answerChoice": {
      const choiceResult = getCommandArg<typeof methodName>(event);
      const { skip } = choiceResult;
      const { executor } = game;
      const effect = executor.getEffectContext();
      if (!effect) return null;
      if (
        Proposition.isValueProposition(effect) &&
        effect.methodName === "chooseGoToBattle"
      ) {
        const result = Choice.getChoiceResultArg<
          typeof AgentEffectChoices,
          "canDeployToConflict"
        >(
          choiceResult as ChoiceResult<
            typeof AgentEffectChoices,
            "canDeployToConflict"
          >
        );
        if (!result?.troopCount) return null;
        return (
          <>
            deploys {result.troopCount}{" "}
            {result.type === "troop" ? (
              <XPlayerTroop
                width={24}
                playerId={playerId}
                style={{ verticalAlign: "middle", display: "inline-block" }}
              />
            ) : (
              <XPlayerDreadnought
                playerId={playerId}
                width={24}
                style={{ verticalAlign: "middle", display: "inline-block" }}
              />
            )}{" "}
            to the conflict
          </>
        );
      }

      const choice = executor.getNextChoiceForPlayer(playerId);
      if (!choice) {
        throw new Error("unexpected: no choice");
      }
      const { type: choiceType } = choice;
      return (
        <>
          in the context of the effect,
          <div
            style={{
              width: "fit-content",
              padding: 5,
              borderRadius: 3,
              marginTop: Spacing.px4,
              marginBottom: Spacing.px12,
              background:
                "linear-gradient(180deg, #9E9893 0%, #A19589 19.79%, #A39385 50%, #A29489 79.17%, #9E9893 100%)",
            }}
          >
            <XAgentEffect
              effectDescription={effect}
              width={32}
              context="card"
            />
          </div>
          chooses:
          <XButton style={{ width: "fit-content" }}>
            {(() => {
              if (skip) return <>skipped the effect</>;
              const { value } = choiceResult;
              return (
                <XChoice
                  value={value}
                  playerId={playerId}
                  choiceType={choiceType}
                />
              );
            })()}
          </XButton>
        </>
      );
    }
    case "playGlobalEffect": {
      return (
        <>
          uses its faction power:{" "}
          <XAgentEffect
            context="card"
            effectDescription={player.playerLeader.globalEffect}
            width={32}
            color={Colors.common.black}
          />
        </>
      );
    }
    case "useTech": {
      const techId = getCommandArg<typeof methodName>(event);
      const tech = game.getTechTile(techId, true);
      return (
        <>
          uses <XTechTile techTile={tech} />
        </>
      );
    }
    case "askUndo": {
      return <>asks to cancel last action</>;
    }
    case "answerUndo": {
      const { isOk } = getUndoCommandArg<typeof methodName>(event);
      return <>answers {isOk ? "'Yes'" : "'No'"} to cancel last action</>;
    }
    default: {
      expectNever(methodName);
      throw new Error();
    }
  }
};

const XEventCard = ({ card, size }: { card: Card; size: number }) => {
  return (
    <div style={{ display: "inline-block", verticalAlign: "middle" }}>
      <XCard card={card} width={size} removeAnimationId={true} />
    </div>
  );
};
